<script setup lang="ts">
import consola from 'consola'
import { useCloned } from '~/composables/Cloned'
import { isEqual } from '~/utils/is'

const { t, locale } = useI18n()

const localPath = useLocalePath()
const { id } = useRouteParams()
const { formatQuery } = useUseBeforeRequest()

const authStore = useAuthStore()
const cartStore = useCartStore()
const categoriesStore = useCategoryStore()
const { addCart, submitLoading } = useAddCart()
const user = useState<_JwtPayload | null>('user', () => null)

const userinfo = computed(() => {
  try {
    return user.value
  }
  catch (error) {
    consola.error(error)
    return authStore.userinfo
  }
})

const priceFiled = computed(() => {
  try {
    const priceChar = authStore.priceFiled || 'aprice'
    return priceChar
  }
  catch (error) {
    consola.error(error)
    return 'aprice'
  }
})

const isLogin = computed(() => !!userinfo.value?.id)

const { data } = await useFetch<_ResponseType<_Detail>>(`/api/commodity/stockMasterEnquiryNew`, {
  params: {
    MESSAGE: formatQuery({ itemCode: id }),
  },
  dedupe: 'cancel',
})

const details = computed<_ProductDetail>(() => {
  if (!data.value?.success) {
    return {} as _ProductDetail
  }
  const { StockExtraInfoEnquiry, StockMasterEnquiry } = data.value!.data!

  const tempData = {
    ...StockExtraInfoEnquiry[0],
    ...StockMasterEnquiry[0],
  } as _ProductDetail

  tempData.activePrice = tempData[priceFiled.value]

  return {
    ...data.value.data,
    ...tempData,
  }
})

const correlation = computed<_Correlation[]>(() => {
  return details.value?.correlation || []
})

const isTool = computed<boolean>(() => {
  return hasOwn(details.value, 'productType') && details.value.productType === 1
})

const cookBooks = computed<_Detail['cookBooks']>(() => {
  return details.value?.cookBooks || []
})

const images = computed<string[]>(() => {
  const { path1, path2, path3, path4 } = details.value
  return [path1, path2, path3, path4].filter(Boolean)
})

const hashTag = computed<{ name: string, type: string, link: string }[]>(() => {
  if (!details.value?.hashTag) {
    return []
  }
  // 以 $ 分割 前面的是产品 后面的是食谱
  const hashTagArr = details.value.hashTag?.replace(/\s/g, '').split('$')
  if (hashTagArr.length === 0) {
    return []
  }

  const recipesTags = hashTagArr[1]?.replace(/^#/, '').split('#')
  const recipes = recipesTags?.map((item: string) => ({
    name: item,
    type: 'recipe',
    link: `/hashTag/${item}`,
  })) || []

  const productTags = hashTagArr[0]?.replace(/^#/, '').split('#')
  const products = productTags?.map((item: string) => ({
    name: item,
    type: 'product',
    link: `/hashTag/${item}`,
  })) || []

  return [...products, ...recipes]
})

const { cloned: cartList } = useCloned<_CartItem[]>(cartStore.cart)

/** 加入购物车 */
async function handleJoinCart() {
  await addCart({
    count: 1,
    cat: details.value.cat,
    scode: details.value.scode,
    img: details.value.path1,
    price: details.value.activePrice,
    name: details.value.cdescript,
    nameEn: details.value.descript,
  })
}

/** 查询改商品是否在购物车 */
const isInCart = computed<boolean>(() => {
  // 从普通商品中查找是否有该商品
  return cartStore.cart.filter(item => !item.cookbookId).some(item => item.scode === details.value.scode)
})
/** 购物车数量 */
const productNum = computed({
  get() {
    if (isInCart.value) {
      // 从普通商品中查找商品
      const product = cartStore.cart.filter(item => !item.cookbookId).find(item => item.scode === details.value.scode)
      return product?.count || 1
    }
    else {
      return 1
    }
  },
  async set(val) {
    if (isInCart.value) {
      // 从普通商品中查找商品
      const product = cartStore.cart.filter(item => !item.cookbookId).find(item => item.scode === details.value.scode) as _CartItem
      product.count = val

      if (product.count === 0) {
        if (userinfo.value) {
          await $fetch('/ichi_api/shoppingCart/delShoppingCart', {
            method: 'post',
            params: { MESSAGE: formatQuery({ id: product.id }) },
          })
          cartStore.removeCart(product.id)
        }
        else {
          useHttp.delete('/api/cart/delete', [product.scode]).then(async () => {
            await cartStore.syncCart()
          })
        }
      }
    }
  },
})

const bayLoading = ref<boolean>(false)
/** 立即购买 */
async function handleBuyNow() {
  // 加入数量后跳转购物车页面

  try {
    bayLoading.value = true
    if (!userinfo.value) {
      await useHttp.put<any>('/api/cart/sync', {
        count: productNum.value,
        cat: details.value.cat,
        scode: details.value.scode,
        img: details.value.path1,
        price: details.value.activePrice,
        name: details.value.cdescript,
      })
      await cartStore.syncCart()
    }
    else {
      const product = cartStore.cart.filter(item => !item.cookbookId).find(item => item.scode === details.value.scode) as _CartItem
      if (product) {
        await useHttp.post<any>('/ichi_api/shoppingCart/edit', null, {
          params: {
            MESSAGE: formatQuery({
              id: product.id,
              count: product.count,
            }),
          },
        })
      }
      else {
        await handleJoinCart()
      }
    }

    await nextTick()
    submitLoading.value = false

    await nextTick()
    bayLoading.value = false

    navigateTo(localPath('/cart'))
  }
  catch (error: any) {
    consola.error(error)
    showNotify(error.msg || error.message || error)
  }
  finally {
    bayLoading.value = false
  }
}

// 页面刷新前保存购物车 f5 ctrl+r 地址栏刷新
function beforeRefresh(event: BeforeUnloadEvent) {
  // 阻止页面刷新，完成后在刷新
  // 深度对比两个数组对象是否一致
  if (!isEqual(cartList.value, cartStore.cart)) {
    cartStore.putCart(cartStore.cart)
    event.preventDefault()
  }
}

onMounted(() => {
  window.addEventListener('beforeunload', beforeRefresh)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', beforeRefresh)
})

onBeforeRouteLeave((to, _from, next) => {
  window.removeEventListener('beforeunload', beforeRefresh)
  cartStore.putCart(cartStore.cart)
  next()
})

// 返回 2025-01-08更改逻辑：返回到改商品对应的分类
// const onClickLeft = () => history.back()
function onClickLeft() {
  /**    一级分类 二级   */
  const { cat } = details.value
  let uri = `/category`
  // 根据 一级分类找到对应的大分类
  const oneCat = categoriesStore.subCategories(1).find(item => item.code === cat) as _CategoryStateItem
  const bigCat = categoriesStore.topCategories.value.find(item => item.id === oneCat.parentId) as _CategoryStateItem

  uri += `/${bigCat.code}/${oneCat.code}`
  // const twoCat = categoriesStore.subCategories(2).find(item => item.code === cat2) as _CategoryStateItem

  // if (twoCat) {
  //   uri += `/${twoCat.code}`
  // }
  navigateTo(localPath(uri), { replace: true })
}

const title = computed(() => {
  const { cat } = details.value
  const oneCat = categoriesStore.subCategories(1).find(item => item.code === cat) as _CategoryStateItem
  const bigCat = categoriesStore.topCategories.value.find(item => item.id === oneCat.parentId) as _CategoryStateItem

  if (locale.value === 'en') {
    return `${bigCat.nameEn} / ${oneCat.nameEn}`
  }
  else {
    return `${bigCat.name} / ${oneCat.name}`
  }
})

const moreContent = ref()

const { data: seoData, status: seoStatus } = await useFetch<_Seo[]>('/api/seoRelation/list', {
  method: 'POST',
  body: {
    paths: [useRoute().path],
  },
})

watch(() => seoStatus.value, (newVal) => {
  if (newVal === 'success') {
    if (seoData.value && Array.isArray(seoData.value) && seoData.value.length) {
      const { content } = JSON.parse(JSON.stringify(seoData.value)).pop()
      moreContent.value = content.content
      const { title, meta } = content
      useHead({
        title,
        meta,
      })
    }
  }
}, {
  immediate: true,
})
</script>

<template>
  <div>
    <with-header left-arrow with-search :client-left="onClickLeft" />
    <!-- <app-navbar
      :border="false"
      left-arrow fixed
      @click-left="onClickLeft"
    /> -->
    <div class="main">
      <van-swipe
        :loop="false"
        :height="365"
        class="custom-swipe"
      >
        <van-swipe-item
          v-for="image in images"
          :key="image"
          class="custom-swipe-item"
        >
          <van-image w-full fit="contain" position="center" :src="image" />
        </van-swipe-item>
        <template #indicator="{ active, total }">
          <div class="custom-indicator">
            {{ active + 1 }}/{{ total }}
          </div>
        </template>
      </van-swipe>
      <div class="mt-9px bg-[#fff] p-17px">
        <div
          flex
          items-center
        >
          <client-only>
            <price class="mr-3 font-size-23px text-[#743a3a] font-500" :price="details.activePrice" />
          </client-only>
          <div v-if="!isLogin" class="font-size-14px text-[#c96865]">
            {{ t("Log in as a member to enjoy more benefits") }}
          </div>
        </div>
        <div
          v-if="locale === 'en'"
          my-1 font-size-18px font-500
        >
          {{ details.descript }}
        </div>
        <div
          v-else
          my-1 font-size-18px font-500
        >
          {{ details.cdescript }}
        </div>
        <div
          font-size-14px
          class="text-[#666666]"
        >
          {{ t("Product No") }}：{{ details.scode }}
        </div>
        <div
          font-size-14px
          class="text-[#666666]"
        >
          {{ t("pages.product.category") }}：{{ title }}
        </div>
      </div>
      <div v-if="details.coupText" class="product-discount flex flex-col gap-y-4 text-sm">
        <div v-if="details.coupText.coupMText" class="bg-theme text-white font-600">
          {{ locale === 'en' ? details.coupText.coupMTextEn : details.coupText.coupMText }}
        </div>
        <div v-if="details.coupText.coupText" class="bg-theme text-white font-600">
          {{ locale === 'en' ? details.coupText.coupTextEn : details.coupText.coupText }}
        </div>
      </div>
      <div
        v-if="hashTag.length"
        class="product-discount"

        flex flex-row flex-wrap gap-2.5 font-size-12px
      >
        <van-button
          v-for="item in hashTag"
          :key="item.link"
          size="small"
          plain
          round
          color="#743a3a"
          :to="item.link"
        >
          #{{ item.name }}
        </van-button>
      </div>
      <div class="product-discount">
        <div flex>
          <svgo-juxing
            filled
            class="text-xl"
          />
          <div

            font-size-14px font-600
          >
            {{ t("Product introduction") }}
          </div>
        </div>
        <div class="product-introduction">
          {{ details.f16 }}
        </div>
      </div>
      <div class="product-discount">
        <div flex>
          <svgo-juxing
            filled
            class="text-xl"
          />
          <div

            font-size-14px font-600
          >
            {{ t("Product details") }}
          </div>
        </div>
        <div class="ml-4.5 mt-4 flex flex-col gap-5 text-sm color-[#666666]">
          <div class="flew-row flex gap-8">
            <div class="basis-35%">
              {{ t("Product No") }}
            </div>
            <div flex-1>
              {{ details.scode }}
            </div>
          </div>
          <div class="flew-row flex gap-8">
            <div class="basis-35%">
              {{ t("Content/Net Weight") }}
            </div>
            <div flex-1>
              {{ details.f04 }}
            </div>
          </div>
          <div class="flew-row flex gap-8">
            <div class="basis-35%">
              {{ t("Origin") }}
            </div>
            <div flex-1>
              {{ details.f06 }}
            </div>
          </div>
          <div class="flew-row flex gap-8">
            <div class="basis-35%">
              {{ t("Stock status") }}
            </div>
            <div flex-1>
              {{ details.f02 }}
            </div>
          </div>
          <div class="flew-row flex gap-8">
            <div class="basis-35%">
              {{ t("Product nickname") }}
            </div>
            <div flex-1>
              {{ details.f17 }}
            </div>
          </div>
          <div class="flew-row flex gap-8">
            <div class="basis-35%">
              {{ t("Storage method/precautions") }}
            </div>
            <div flex-1>
              {{ details.f05 }}
            </div>
          </div>
        </div>
      </div>
      <div class="product-discount">
        <div flex>
          <svgo-juxing
            filled
            class="text-xl"
          />
          <div font-size-14px font-600>
            <span v-if="!isTool">
              {{ t("Food ingredients") }}
            </span>
            <span v-else>
              {{ t('product.shop_info') }}
            </span>
          </div>
        </div>
        <div class="product-introduction">
          {{ details.m03 }}
        </div>
      </div>
      <div v-if="!isTool" class="product-discount">
        <div flex>
          <svgo-juxing
            filled
            class="text-xl"
          />
          <div

            font-size-14px font-600
          >
            {{ t("Nutritional information") }}
          </div>
        </div>
        <div class="nutrition-table">
          <table>
            <tbody>
              <tr>
                <td>{{ t('energy') }}</td>
                <td>{{ details.f07 }}</td>
              </tr>
              <tr>
                <td>{{ t('protein') }}</td>
                <td>{{ details.f08 }}</td>
              </tr>
              <tr>
                <td>{{ t('total fat') }}</td>
                <td>{{ details.f09 }}</td>
              </tr>
              <tr>
                <td>{{ t('saturated fat') }}</td>
                <td>{{ details.f10 }}</td>
              </tr>
              <tr>
                <td>{{ t('trans fat') }}</td>
                <td>{{ details.f11 }}</td>
              </tr>
              <tr>
                <td>{{ t('carbohydrate') }}</td>
                <td>{{ details.f12 }}</td>
              </tr>
              <tr>
                <td>{{ t('sugar') }}</td>
                <td>{{ details.f13 }}</td>
              </tr>
              <tr>
                <td>{{ t('sodium') }}</td>
                <td>{{ details.f14 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="product-discount">
        <div flex>
          <svgo-juxing
            filled
            class="text-xl"
          />
          <div

            font-size-14px font-600
          >
            {{ t("How to use/make") }}
          </div>
        </div>
        <div class="product-introduction">
          {{ details.m01 }}
        </div>
      </div>
      <div class="product-discount">
        <div flex>
          <svgo-juxing
            filled
            class="text-xl"
          />
          <div

            font-size-14px font-600
          >
            {{ t("Related products") }}
          </div>
        </div>
        <div class="related-products">
          <product-list :products="correlation" />
        </div>
      </div>
      <div v-if="cookBooks!.length" class="product-discount">
        <div flex>
          <svgo-juxing filled class="text-xl" />
          <div font-size-14px font-600>
            {{ t("Related recipes") }}
          </div>
        </div>
        <div class="related-products">
          <div
            v-for="item in cookBooks"
            :key="item.id"
          >
            <NuxtLink prefetch :to="localPath(`/recipe/${item.id}`)">
              <van-image
                rounded
                :src="item.coverImgUrl"
                :alt="item.name"
              />
            </NuxtLink>
            <div
              mt-1
              font-size-14px
            >
              {{ item.name }}
            </div>
          </div>
        </div>
      </div>
      <van-action-bar safe-area-inset-bottom placeholder>
        <van-action-bar-icon :badge="cartStore.total" @click="navigateTo(localPath('/cart'))">
          <template #icon>
            <svgo-addcar
              filled
              class="text-2xl"
            />
          </template>
        </van-action-bar-icon>
        <template v-if="!isInCart">
          <van-action-bar-button
            color="rgba(116, 58, 58, .8)"
            :text="t('add to cart')"
            :loading="submitLoading"
            @click="handleJoinCart"
          />
        </template>
        <template v-else>
          <!-- 加减数量的操作 -->
          <van-stepper
            v-model="productNum"
            class="ml-4"
            theme="round"
            button-size="28"
            disable-input
            :min="0"
          />
        </template>
        <van-action-bar-button :loading="bayLoading" color="#743a3a" type="danger" :text="t('Buy it now')" @click="handleBuyNow" />
      </van-action-bar>
    </div>
  </div>
</template>

<style scoped lang="scss">
.main {
  background-color: #f7f7f7;
  margin-top: 46px;
  height: calc(100vh - 60px);
  overflow-y: auto;
}

:deep(.van-swipe-item) {
  img {
    max-height: 365px;
  }
}

.custom-swipe {
  background-color: #fff;
}

.custom-indicator {
  position: absolute;
  width: 40px;
  right: 5px;
  bottom: 5px;
  padding: 2px 10px;
  font-size: 12px;
  color: #fff;
  background: #a6a6a6;
  border-radius: 10px;
}
.product-discount {
  background-color: #fff;
  margin-top: 9px;
  padding: 17px;
  .product-introduction {
    margin-left: 18px;
    margin-top: 10px;
    font-size: 14px;
    color: #666666;
    // word-break: keep-all;
    text-wrap: pretty;
  }
}

.nutrition-table {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  border-collapse: collapse;
  font-family: Arial, sans-serif;
}

.nutrition-table table {
  width: 100%;
  margin-top: 10px;
  margin-left: 0.5rem;
  border: 1px solid #ccc;
  overflow: hidden;
  border-radius: 2px;
  color: #666666;
  font-weight: bold;
}

.nutrition-table td {
  padding: 12px 0;
  box-sizing: border-box;
  border-bottom: 1px solid #f0f0f0;
  text-align: center;
  font-size: 14px;
}

.nutrition-table td:first-child {
  width: 100px;
  background-color: #f8f9fa;
}

.related-products {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 15px;
  // padding: 0 20px;
}
:deep(.van-action-bar-button--last) {
  margin-right: 30px;
  border-top-right-radius: 999px;
  border-bottom-right-radius: 999px;
  height: min(var(--van-action-bar-button-height), 40px);
  font-size: var(--van-font-size-md);
}
:deep(.van-action-bar-button--first) {
  margin-left: 30px;
  border-top-left-radius: 999px;
  border-bottom-left-radius: 999px;
  height: min(var(--van-action-bar-button-height), 40px);
  font-size: var(--van-font-size-md);
}
</style>
